import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CampaignStatusEnum } from '../../enum/campaign-status';

@Component({
  selector: 'app-approbation-progress',
  templateUrl: './approbation-progress.component.html',
  styleUrls: ['./approbation-progress.component.scss']
})
export class ApprobationProgressComponent implements OnInit {

  @Output() closeTheModal: EventEmitter<boolean> = new EventEmitter();
  @Input() campaignName!: string;
  @Input() campaignStatus!: CampaignStatusEnum;
  @Input() isLocation: boolean = false;
  @Input() isActiveLocation: boolean = true;
  @Input() locationName: string = "";

  steps: CampaignStatusEnum[] = [
    CampaignStatusEnum.DRAFT,
    CampaignStatusEnum.PENDING_FRANCHISE_APPROVAL,
    CampaignStatusEnum.PENDING_CONFIRMATION,
    CampaignStatusEnum.AWAITING_APPROVAL,
  ];

  completedCount = 0;

  // convenience flags
  private cancelMode: 'none' | 'generic' | 'franchise' | 'admin' = 'none';
  private cancelIndex = -1;

  constructor() {}

  ngOnInit(): void {
    // figure out cancel mode + where it failed
    switch (this.campaignStatus) {
      case CampaignStatusEnum.CANCELED:
        this.cancelMode = 'generic';
        this.cancelIndex = this.steps.length - 1; // last step will be X
        break;
      case CampaignStatusEnum.CANCELED_BY_FRANCHISE:
        this.cancelMode = 'franchise';
        this.cancelIndex = this.steps.indexOf(CampaignStatusEnum.PENDING_FRANCHISE_APPROVAL);
        break;
      case CampaignStatusEnum.CANCELED_BY_ADMIN:
        this.cancelMode = 'admin';
        this.cancelIndex = this.steps.indexOf(CampaignStatusEnum.PENDING_CONFIRMATION);
        break;
      default:
        this.cancelMode = 'none';
        this.cancelIndex = -1;
        break;
    }

    if (this.isCanceled()) {
      // everything strictly before the cancel point is considered completed
      // (the cancel step itself will be marked as warning, and the last step as X)
      this.completedCount = Math.max(0, this.cancelIndex);
    } else {
      // normal flow: progress up to current status
      const idx = this.steps.indexOf(this.campaignStatus);
      this.completedCount = idx >= 0 ? idx : this.steps.length;
    }
  }

  // ---- state helpers ----
  isCanceled(): boolean {
    return this.cancelMode !== 'none';
  }

  isCompleted(i: number): boolean {
    if (this.isCanceled()) return i < this.completedCount;
    return i < this.completedCount;
  }

  isCurrent(i: number): boolean {
    if (this.isCanceled()) return false; // no "current" in canceled flows
    return i === this.completedCount;
  }

  isWarning(i: number): boolean {
    // the step where approval failed (franchise/admin) shows a red "!"
    return this.isCanceled() && this.cancelMode !== 'generic' && i === this.cancelIndex;
  }

  isFailed(i: number): boolean {
    // final step is an X for any canceled flow
    return this.isCanceled() && i === (this.steps.length - 1);
  }

  isMuted(i: number): boolean {
    // steps after the failing point until (but not including) the final X are dimmed
    return this.isCanceled() && i > this.cancelIndex && i < (this.steps.length - 1);
  }

  getTitle(status: CampaignStatusEnum, idx: number): string {
    if (this.isCanceled() && idx === this.steps.length - 1) {
      return `enumMapping.campaigns.status.approbation.CANCELED.title`
    }
    if (!this.isCanceled() && this.isCompleted(idx) && idx === this.steps.length - 1) {
      return `enumMapping.campaigns.status.approbation.APPROVED.title`
    }
    if (!this.isCanceled() && !this.isCompleted(idx) && idx === this.steps.length - 1) {
      return `enumMapping.campaigns.status.approbation.NOT_YET_APPROVED.title`
    }
    return `enumMapping.campaigns.status.approbation.${status}.title`;
  }

  getDescription(status: CampaignStatusEnum, idx: number): string {
    if (this.isCanceled() && idx === this.steps.length - 1) {
      return `enumMapping.campaigns.status.approbation.CANCELED.description`
    }
    if (!this.isCanceled() && this.isCompleted(idx) && idx === this.steps.length - 1) {
      return `enumMapping.campaigns.status.approbation.APPROVED.description`
    }
    if (!this.isCanceled() && !this.isCompleted(idx) && idx === this.steps.length - 1) {
      return `enumMapping.campaigns.status.approbation.NOT_YET_APPROVED.description`
    }
    return `enumMapping.campaigns.status.approbation.${status}.description`;
  }

  closeModal() {
    this.closeTheModal.emit(true);
  }
}
